import React, { useCallback, useEffect, useRef, useState } from 'react'
import { speechService } from '../../services/speech'
import { translateWordAPI, translateSentenceAPI, speakJapaneseText, convertToRomajiAPI } from '../../services/api'
import { useUserStore } from '../../store/userStore'
import { useLanguageStore } from '../../store/languageStore'
import { getLanguageByCode } from '../../constants/languages'
import type { LanguageCode } from '../../services/languageApi.js'
import * as wanakana from 'wanakana'

interface ConversationExchange {
  id: number
  speaker: 'user' | 'ai'
  textContent: string
  timestamp: string
  pronunciationFeedback?: string | null
  transcriptionConfidence?: number | null
  learningNotes?: string | null
  audioUrl?: string
}

type PronunciationMode = 'off' | 'romaji' | 'english' | 'hiragana' | 'symbols'

interface ConversationDisplayProps {
  exchanges: ConversationExchange[]
  isWaitingForAI?: boolean
  difficultyLevel?: 'beginner' | 'intermediate' | 'advanced'
  pronunciationMode?: PronunciationMode
  setPronunciationMode?: (mode: PronunciationMode) => void
}

const PRONUNCIATION_LANGUAGES: LanguageCode[] = ['ja', 'zh', 'hi']
const PRONUNCIATION_LANGUAGE_SET = new Set(PRONUNCIATION_LANGUAGES)
const DEFAULT_TTS_LOCALE = 'ja-JP'

const getLocaleForLanguage = (languageCode?: string | null): string => {
  if (!languageCode) return DEFAULT_TTS_LOCALE
  if (languageCode === 'en') return 'en-US'
  return getLanguageByCode(languageCode)?.ttsLanguageCode || DEFAULT_TTS_LOCALE
}

const KANA_CHAR_REGEX = /^[\u3040-\u309F\u30A0-\u30FF\uFF65-\uFF9Fー]+$/
const PUNCTUATION_REGEX = /^[\s、。.,!?！？ー～「」『』（）()【】\[\]{}・：；…-]+$/
const SPLIT_REGEX = /(\s+|[、。.,!?！？ー～「」『』（）()【】\[\]{}・：；…-])/

const removeInlinePronunciation = (text: string): string =>
  text
    .replace(/（[\u3040-\u30FF\uFF65-\uFF9Fー\s]+）/g, '')
    .replace(/\([\u3040-\u30FF\uFF65-\uFF9Fー\s]+\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()

const mergeKanaSegments = (
  segments: Array<{ segment: string; isWordLike: boolean }>
): Array<{ segment: string; isWordLike: boolean }> => {
  const merged: Array<{ segment: string; isWordLike: boolean }> = []
  let kanaBuffer = ''

  const flushBuffer = () => {
    if (kanaBuffer) {
      merged.push({ segment: kanaBuffer, isWordLike: true })
      kanaBuffer = ''
    }
  }

  segments.forEach((segment) => {
    const part = segment.segment
    if (!part) return

    const isKanaWord = segment.isWordLike && KANA_CHAR_REGEX.test(part)
    if (isKanaWord) {
      kanaBuffer += part
    } else {
      flushBuffer()
      merged.push(segment)
    }
  })

  flushBuffer()
  return merged
}

const englishSyllableMap: Record<string, string> = {
  shi: 'shee',
  chi: 'chee',
  tsu: 'tsoo',
  ji: 'jee',
  jya: 'jah',
  jyu: 'joo',
  jyo: 'joe',
  ja: 'jah',
  ju: 'joo',
  jo: 'joe',
  rya: 'ree-ah',
  ryu: 'ree-oo',
  ryo: 'ree-oh',
  sha: 'shah',
  shu: 'shoo',
  sho: 'show',
  cha: 'chah',
  chu: 'choo',
  cho: 'choh',
  kya: 'kyah',
  kyu: 'kyoo',
  kyo: 'kyoh',
  gya: 'gyah',
  gyu: 'gyoo',
  gyo: 'gyoh',
  bya: 'byah',
  byu: 'byoo',
  byo: 'byoh',
  pya: 'pyah',
  pyu: 'pyoo',
  pyo: 'pyoh',
  mya: 'myah',
  myu: 'myoo',
  myo: 'myoh',
  fu: 'foo',
}

const englishSyllableEntries = Object.entries(englishSyllableMap).sort(
  (a, b) => b[0].length - a[0].length
)

const vowelPhonetics: Record<string, string> = {
  a: 'ah',
  i: 'ee',
  u: 'oo',
  e: 'eh',
  o: 'oh',
}

function convertRomajiToEnglishPronunciation(input: string): string {
  if (!input) return ''
  return input
    .split(/(\s+|-)/)
    .map((segment) => {
      if (!segment.trim() || segment === '-') {
        return segment
      }

      const lower = segment.toLowerCase()
      if (englishSyllableMap[lower]) {
        return englishSyllableMap[lower]
      }

      let converted = ''
      let idx = 0

      while (idx < lower.length) {
        const remaining = lower.slice(idx)
        const matchedEntry = englishSyllableEntries.find(([pattern]) =>
          remaining.startsWith(pattern)
        )

        if (matchedEntry) {
          converted += matchedEntry[1]
          idx += matchedEntry[0].length
          continue
        }

        const char = lower[idx]
        converted += vowelPhonetics[char] || char
        idx++
      }

      return converted
    })
    .join('')
}

// Component to display pronunciation for a word (handles async loading)
function PronunciationWord({
  text,
  mode,
  getRomaji,
  languageCode,
}: {
  text: string
  mode: Exclude<PronunciationMode, 'off'>
  getRomaji: (text: string) => Promise<string>
  languageCode: LanguageCode
}) {
  const [displayText, setDisplayText] = useState<string>('...')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const loadPronunciation = async () => {
      setLoading(true)

      try {
        let result = text

        if (mode !== 'symbols') {
          const romajiValue = await getRomaji(text)
          if (cancelled) return

          if (mode === 'romaji') {
            result = romajiValue
          } else if (mode === 'english') {
            result = languageCode === 'ja' ? convertRomajiToEnglishPronunciation(romajiValue) : romajiValue
          } else if (mode === 'hiragana') {
            result = languageCode === 'ja' ? wanakana.toHiragana(romajiValue) : romajiValue
          }
        }

        if (!cancelled) {
          setDisplayText(result)
        }
      } catch (err) {
        console.error('Failed to fetch pronunciation:', err)
        if (!cancelled) {
          setDisplayText(text)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadPronunciation()

    return () => {
      cancelled = true
    }
  }, [text, mode, getRomaji])

  return (
    <span className="pronunciation-word-text">
      {loading ? '...' : displayText}
    </span>
  )
}

export function ConversationDisplay({
  exchanges,
  isWaitingForAI = false,
  difficultyLevel = 'intermediate',
  pronunciationMode: propPronunciationMode,
  setPronunciationMode: propSetPronunciationMode
}: ConversationDisplayProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const [playingAudioId, setPlayingAudioId] = useState<number | null>(null)
  const [selectedWord, setSelectedWord] = useState<{
    word: string;
    translation: string;
    position: { x: number; y: number };
    flashcardCreated?: boolean;
  } | null>(null)
  const [loadingTranslation, setLoadingTranslation] = useState(false)
  const [localPronunciationMode, setLocalPronunciationMode] = useState<PronunciationMode>('off')
  const pronunciationMode = propPronunciationMode ?? localPronunciationMode
  const setPronunciationMode = propSetPronunciationMode ?? setLocalPronunciationMode
  const romajiCacheRef = useRef<Record<string, string>>({})
  const [sentenceTranslations, setSentenceTranslations] = useState<Record<number, string>>({})
  const [loadingSentenceTranslation, setLoadingSentenceTranslation] = useState<number | null>(null)
  const [openNotes, setOpenNotes] = useState<Record<number, boolean>>({})
  const user = useUserStore((s) => s.user)
  const currentLanguageCode = useLanguageStore((s) => s.currentLanguage)
  const isJapanese = currentLanguageCode === 'ja'

  const supportsPronunciation = currentLanguageCode
    ? PRONUNCIATION_LANGUAGE_SET.has(currentLanguageCode as LanguageCode)
    : false

  const currentLocale = getLocaleForLanguage(currentLanguageCode)

  // Auto-scroll to bottom when new exchanges are added
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [exchanges.length])

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setSelectedWord(null)
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  useEffect(() => {
    romajiCacheRef.current = {}
  }, [currentLanguageCode])

  const handlePlayAudio = async (exchange: ConversationExchange) => {
    if (!exchange.audioUrl || playingAudioId === exchange.id) return

    try {
      setPlayingAudioId(exchange.id)
      await speechService.playAudio(exchange.audioUrl)
    } catch (err) {
      console.error('Failed to play audio:', err)
    } finally {
      setPlayingAudioId(null)
    }
  }

  const toggleNotes = (exchangeId: number) => {
    setOpenNotes((prev) => ({
      ...prev,
      [exchangeId]: !prev[exchangeId],
    }))
  }

  const handleWordClick = async (word: string, event: React.MouseEvent) => {
    event.stopPropagation()

    // Validate word before making API call
    const trimmedWord = word.trim()

    // Skip if empty
    if (!trimmedWord) return

    // Skip if it's just punctuation
    const punctuationRegex = /^[\s、。.,!?！？ー～「」『』（）()【】\[\]{}・：；…\-]+$/
    if (punctuationRegex.test(trimmedWord)) return

    // Get click position for tooltip
    const rect = (event.target as HTMLElement).getBoundingClientRect()
    const position = {
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    }

    setLoadingTranslation(true)
    setSelectedWord({ word: trimmedWord, translation: 'Loading...', position })

    try {
      // Call backend API to translate and generate speech in parallel
      const sessionIdStr = (exchanges[0] as any)?.sessionId?.toString?.() || ''
      const userIdToSend = (user?.id ?? '1').toString()
      const ttsProvider: 'google' | 'elevenlabs' =
        user?.subscriptionTier === 'premium' ? 'elevenlabs' : 'google'
      const preferredVoice = ttsProvider === 'elevenlabs' ? user?.preferredVoiceId || undefined : undefined

      const speechData = await speakJapaneseText(trimmedWord, ttsProvider, preferredVoice, currentLocale)
      const translationPayload: any = {
        word: trimmedWord,
        userId: userIdToSend as any,
        sessionId: sessionIdStr,
        targetLanguage: currentLanguageCode || 'ja',
      }

      // Only send audioUrl if we actually got audio back
      if (speechData?.audioUrl) {
        translationPayload.audioUrl = speechData.audioUrl
      }

      const translationData = await translateWordAPI(translationPayload)

      setSelectedWord({
        word: trimmedWord,
        translation: translationData.translation,
        position,
        flashcardCreated: Boolean(translationData?.flashcardCreated),
      })

      // Play the audio for the word
      if (speechData?.audioUrl) {
        await speechService.playAudio(speechData.audioUrl)
      }
    } catch (err) {
      console.error('Failed to translate word:', err)
      setSelectedWord({ word: trimmedWord, translation: 'Translation unavailable', position })
    } finally {
      setLoadingTranslation(false)
    }
  }

  const handleSentenceTranslation = async (exchangeId: number, text: string) => {
    // Toggle off if already showing
    if (sentenceTranslations[exchangeId]) {
      setSentenceTranslations(prev => {
        const newTranslations = { ...prev }
        delete newTranslations[exchangeId]
        return newTranslations
      })
      return
    }

    setLoadingSentenceTranslation(exchangeId)

    try {
      const userIdToSend = (user?.id ?? '1').toString()
      const translationData = await translateSentenceAPI({
        sentence: text,
        userId: userIdToSend as any,
        targetLanguage: currentLanguageCode || 'ja',
      })

      setSentenceTranslations(prev => ({
        ...prev,
        [exchangeId]: translationData.translation
      }))
    } catch (err) {
      console.error('Failed to translate sentence:', err)
      setSentenceTranslations(prev => ({
        ...prev,
        [exchangeId]: 'Translation unavailable'
      }))
    } finally {
      setLoadingSentenceTranslation(null)
    }
  }

  // Fetch pronunciation/romanization from backend when needed
  const getRomaji = useCallback(async (text: string): Promise<string> => {
    const languageKey = currentLanguageCode || 'ja'
    const cacheKey = `${languageKey}::${text}`

    if (romajiCacheRef.current[cacheKey]) {
      return romajiCacheRef.current[cacheKey]
    }

    if (languageKey === 'ja') {
      const simpleRomaji = wanakana.toRomaji(text)
      if (simpleRomaji === text || /[\u4e00-\u9faf]/.test(text)) {
        try {
          const data = await convertToRomajiAPI(text, 'ja')
          const romaji = data.romaji
          romajiCacheRef.current[cacheKey] = romaji
          return romaji
        } catch (err) {
          console.error('Failed to fetch romaji:', err)
          romajiCacheRef.current[cacheKey] = simpleRomaji
          return simpleRomaji
        }
      }
      romajiCacheRef.current[cacheKey] = simpleRomaji
      return simpleRomaji
    }

    if (languageKey === 'zh' || languageKey === 'hi') {
      try {
        const data = await convertToRomajiAPI(text, languageKey)
        const romaji = data.romaji
        romajiCacheRef.current[cacheKey] = romaji
        return romaji
      } catch (err) {
        console.error('Failed to fetch pronunciation:', err)
      }
    }

    romajiCacheRef.current[cacheKey] = text
    return text
  }, [currentLanguageCode])

  const renderSegments = (
    segmentsData: Array<{ segment: string; isWordLike: boolean }>
  ) => {
    const mergedSegments = mergeKanaSegments(segmentsData)

    return (
      <span className="japanese-word-markers">
        {mergedSegments.map((s, index) => {
          const part = s.segment
          if (!part) return null

          if (!s.isWordLike || PUNCTUATION_REGEX.test(part)) {
            return <span key={index}>{part}</span>
          }

          return (
            <ruby
              key={index}
              onClick={(e) => handleWordClick(part, e)}
              className="clickable-word"
            >
              <span className="target-language-word-text">{part}</span>
              {pronunciationMode !== 'off' && supportsPronunciation && (
                <rt>
                  <PronunciationWord
                    text={part}
                    mode={pronunciationMode}
                    getRomaji={getRomaji}
                    languageCode={(currentLanguageCode || 'ja') as LanguageCode}
                  />
                </rt>
              )}
            </ruby>
          )
        })}
      </span>
    )
  }

  // Function to add word markers to text with intelligent segmentation (supports all languages)
  const renderTextWithMarkers = (text: string, languageCode: string = 'ja') => {
    let cleanedText = text
    if (isJapanese) {
      cleanedText = removeInlinePronunciation(text) || text
      // Remove spaces between non-ASCII characters (fixes tokenization artifacts like "こ ん に ち は")
      cleanedText = cleanedText.replace(/([^\x01-\x7E])\s+(?=[^\x01-\x7E])/g, '$1')
    }

    // Use Intl.Segmenter for word boundary detection (supported for all languages)
    if ((Intl as any).Segmenter) {
      const segmenter = new (Intl as any).Segmenter(languageCode, { granularity: 'word' })
      const segments = Array.from(segmenter.segment(cleanedText)) as Array<any>
      const normalizedSegments = segments.map((s) => ({
        segment: s.segment as string,
        isWordLike: Boolean(s.isWordLike),
      }))

      return renderSegments(normalizedSegments)
    }

    // Fallback for browsers without Intl.Segmenter: split by whitespace and common punctuation
    const parts = cleanedText.split(SPLIT_REGEX).filter(Boolean)
    const fallbackSegments = parts.map((part) => ({
      segment: part,
      isWordLike: !PUNCTUATION_REGEX.test(part),
    }))

    return renderSegments(fallbackSegments)
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <style>{`
        .clickable-word {
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .clickable-word:hover .target-language-word-text {
          border-bottom-color: var(--word-underline-hover);
        }
        .japanese-word-markers {
          line-height: 1.5;
          word-spacing: normal;
          letter-spacing: normal;
        }
        ruby {
          display: inline-flex;
          flex-direction: column-reverse;
          vertical-align: bottom;
          align-items: center;
          margin: 0;
          padding: 0;
        }
        rt {
          display: block;
          font-size: 10px;
          line-height: 1;
          color: var(--word-pronunciation);
          font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
          text-align: center;
          margin-bottom: 0px;
          min-height: 12px;
          user-select: none;
        }
        .target-language-word-text {
          font-size: 16px;
          font-weight: 500;
          line-height: 1.2;
          border-bottom: 2px solid var(--word-underline);
          padding-bottom: 1px;
        }
        .word-tooltip {
          position: fixed;
          background: #1f2937;
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 14px;
          z-index: 1000;
          pointer-events: none;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transform: translate(-50%, -100%);
          margin-top: -8px;
        }
        .word-tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-top-color: #1f2937;
        }
        .word-tooltip-word {
          font-weight: 600;
          margin-bottom: 4px;
        }
        .word-tooltip-translation {
          font-size: 13px;
          opacity: 0.9;
        }
        .word-tooltip-created {
          margin-top: 6px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #059669; /* success-600 */
          background: #ecfdf5; /* success-50 */
          border: 1px solid #a7f3d0; /* success-200 */
          border-radius: 9999px;
          padding: 2px 8px;
        }
      `}</style>

      {/* Word translation tooltip */}
      {selectedWord && (
        <div
          className="word-tooltip"
          style={{
            left: `${selectedWord.position.x}px`,
            top: `${selectedWord.position.y}px`,
          }}
        >
          <div className="word-tooltip-word" lang={currentLanguageCode || 'ja'}>{selectedWord.word}</div>
          <div className="word-tooltip-translation">
            {loadingTranslation ? 'Loading...' : selectedWord.translation}
          </div>
          {selectedWord.flashcardCreated && !loadingTranslation && (
            <div className="word-tooltip-created">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Added to flashcards</span>
            </div>
          )}
        </div>
      )}

      {exchanges.length === 0 ? (
        <div className="text-center text-gray-500 mt-8">
          <p className="text-lg">Start speaking to begin your conversation</p>
          <p className="text-sm mt-2">Press the microphone button or type your message</p>
        </div>
      ) : (
        <>
          {exchanges.map((exchange) => (
            <div
              key={exchange.id}
              className={`flex ${exchange.speaker === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] px-3 py-1 rounded-2xl shadow-md border ${exchange.speaker === 'user'
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white border-transparent rounded-br-md'
                  : 'bg-[var(--color-surface)] text-[var(--color-text)] border-[var(--color-border)] rounded-bl-md'
                  }`}
              >
                <div className="flex items-start space-x-2">
                  {exchange.speaker === 'ai' && (
                    <span className="text-2xl flex-shrink-0">🤖</span>
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className="text-base target-language-text whitespace-pre-wrap flex-1 leading-none"
                        lang={currentLanguageCode || 'ja'}
                      >
                        {exchange.speaker === 'ai'
                          ? renderTextWithMarkers(exchange.textContent, currentLanguageCode || 'ja')
                          : exchange.textContent
                        }
                      </p>

                      {exchange.speaker === 'ai' && exchange.audioUrl && (
                        <button
                          onClick={() => handlePlayAudio(exchange)}
                          disabled={playingAudioId === exchange.id}
                          className="flex-shrink-0 p-1.5 rounded-full text-[var(--color-text)] bg-[var(--color-surface-secondary)]/50 hover:bg-[var(--color-surface-secondary)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Play audio"
                        >
                          {playingAudioId === exchange.id ? (
                            <svg className="w-4 h-4 text-primary-600 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>

                    {exchange.speaker === 'user' && exchange.transcriptionConfidence !== null && (
                      <div className="mt-2 text-xs opacity-80">
                        Confidence: {Math.round(exchange.transcriptionConfidence * 100)}%
                      </div>
                    )}

                    {exchange.pronunciationFeedback && (
                      <div className="mt-2 pt-2 border-t border-primary-200 text-xs text-[var(--color-text)]">
                        💡 {exchange.pronunciationFeedback}
                      </div>
                    )}

                    {exchange.speaker === 'user' && exchange.learningNotes && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => toggleNotes(exchange.id)}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs border border-amber-300 rounded-lg bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20h9M3 20h7m2-5v5m0-5l6-8H6l6 8z" />
                          </svg>
                          {openNotes[exchange.id] ? 'Hide Notes' : 'View Notes'}
                        </button>
                        {openNotes[exchange.id] && (
                          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 whitespace-pre-line">
                            {exchange.learningNotes}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Translation button for AI messages */}
                    {exchange.speaker === 'ai' && (
                      <div className="mt-3">
                        <button
                          onClick={() => handleSentenceTranslation(exchange.id, exchange.textContent)}
                          disabled={loadingSentenceTranslation === exchange.id}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[var(--color-surface-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-secondary)]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                          </svg>
                          {loadingSentenceTranslation === exchange.id
                            ? 'Translating...'
                            : sentenceTranslations[exchange.id]
                              ? 'Hide Translation'
                              : 'Show Translation'}
                        </button>
                        {sentenceTranslations[exchange.id] && (
                          <div className="mt-2 p-3 bg-primary-50 border border-primary-100 rounded-lg">
                            <p className="text-sm text-primary-800 italic">
                              "{sentenceTranslations[exchange.id]}"
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-1 text-xs opacity-70">
                      {new Date(exchange.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                  {exchange.speaker === 'user' && (
                    <span className="text-2xl flex-shrink-0">👤</span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {isWaitingForAI && (
            <div className="flex justify-start">
              <div className="bg-[var(--color-surface-secondary)] border border-[var(--color-border)] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">🤖</span>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <div ref={bottomRef} />
    </div>
  )
}
